import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import Navigation from "./Navigation";
import Footer from "./Footer";
import FloatingVentButton from "./FloatingVentButton";
import { useIsMobile } from "@/hooks/use-mobile";
import { VentLogo } from "./VentLogo";
import NotificationBadge from "./NotificationBadge";
import { Button } from "./ui/button";
import { Settings, ChevronDown, CalendarDays } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useMobileAppState } from "@/hooks/useMobileAppState";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useAppBadge } from "@/hooks/useAppBadge";
import { useBiometricLock } from "@/hooks/useBiometricLock";
import { useNativeStatusBar } from "@/hooks/useNativeStatusBar";
import BiometricGate from "./BiometricGate";
import StreakMilestoneCelebration from "./wellness/StreakMilestoneCelebration";
import HeaderStreakButton from "./wellness/HeaderStreakButton";
import LocationRePrompt from "./LocationRePrompt";
import WeeklyReviewModal from "./engagement/WeeklyReviewModal";
import { CommunityTourContext } from './onboarding/CommunityTourContext';
import { CommunityWelcomeTour } from './onboarding/CommunityWelcomeTour';
import { useCommunityTour } from '@/hooks/useCommunityTour';
import { quietPrompts } from '@/lib/promptCoordinator';

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isNative = useNativePlatform();
  const isMobile = useIsMobile();
  const tour = useCommunityTour(user?.id, location.pathname === '/app');
  useEffect(() => {
    if (!tour.deferred) return;
    quietPrompts(2000);
    const timer = window.setInterval(() => quietPrompts(2000), 1000);
    return () => window.clearInterval(timer);
  }, [tour.deferred]);

  // Hide bottom nav when in an active conversation on mobile/native
  const isChatDetail = (isMobile || isNative) && location.pathname === '/app/messages' && searchParams.has('conversation');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [isSupport, setIsSupport] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [viewMode, setViewMode] = useState<'platform' | 'background'>('platform');

  // Mobile app state tracking - redirects to home if away for 15+ seconds
  useMobileAppState();

  // Sync unread notification count to native app badge
  useAppBadge();

  // Keep iOS/Android status bar icons readable against the current theme background.
  useNativeStatusBar();

  // Biometric lock (Face ID / Touch ID)
  const { isLocked, authenticate } = useBiometricLock();

  // Auto-detect view mode based on current route
  useEffect(() => {
    const backgroundRoutes = ['/app/moderation', '/app/support-dashboard', '/app/analytics', '/app/feedback', '/app/therapist-verification', '/app/knowledge-base'];
    const isBackgroundRoute = backgroundRoutes.some(route => location.pathname.startsWith(route));
    setViewMode(isBackgroundRoute ? 'background' : 'platform');
  }, [location.pathname]);

  useEffect(() => {
    const checkRoles = async () => {
      if (!user) {
        setRolesLoaded(true);
        return;
      }

      const { data: moderatorData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .in('role', ['admin', 'moderator'])
        .maybeSingle();
      setIsModerator(!!moderatorData);

      const { data: adminData } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: 'admin'
      });
      setIsAdmin(!!adminData);

      const { data: supportData } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: 'support'
      });
      setIsSupport(!!supportData);
      
      setRolesLoaded(true);
    };
    checkRoles();
  }, [user]);

  const showAdminDropdown = isAdmin || isModerator || isSupport;

  if (!rolesLoaded) {
    return null;
  }

  // Show biometric lock screen when app is locked
  if (isLocked) {
    return <BiometricGate onAuthenticate={authenticate} />;
  }

  return (
    <CommunityTourContext.Provider value={tour.deferred}>
    <div className="min-h-screen bg-gradient-soft flex flex-col">
      {/* Legacy location recovery prompt */}
      <LocationRePrompt />

      {/* Header */}
      <header className="border-b border-border/50 bg-background/95 backdrop-blur-sm dark:bg-background dark:backdrop-blur-none sticky top-0 z-50">
        <div className="container mx-auto px-3 py-0.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/app" className="flex items-center gap-2">
              <VentLogo />
            </Link>
            <HeaderStreakButton />
          </div>
          <div className="flex items-center gap-3">
            {showAdminDropdown && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    {viewMode === 'platform' ? 'Platform' : 'Background'}
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                  <DropdownMenuItem onSelect={() => {
                    setViewMode('platform');
                    navigate('/app');
                  }}>
                    Platform
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => {
                    setViewMode('background');
                    navigate('/app/admin');
                  }}>
                    Background
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/app/settings')}
              className="h-8 w-8"
            >
              <Settings className="h-4 w-4" />
              <span className="sr-only">Settings</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/app/wellness/calendar')}
              data-community-tour="calendar"
              className="h-8 w-8"
            >
              <CalendarDays className="h-4 w-4" />
              <span className="sr-only">Wellness calendar</span>
            </Button>
            <NotificationBadge />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`flex-1 min-h-0 ${isChatDetail ? '' : 'pb-16'}`}>
        <Outlet />
      </main>
      <StreakMilestoneCelebration />
      <WeeklyReviewModal />
      <CommunityWelcomeTour key={user?.id} pending={tour.pending} step={tour.step} onChange={tour.update} />

      {/* Footer - only show on web/mobile browser, not in native app */}
      {!isNative && (
        <div className="pb-24">
          <Footer />
        </div>
      )}

      {/* Floating Vent Button - hide in chat detail mode */}
      {!isChatDetail && <FloatingVentButton />}

      {/* Bottom Navigation - hide in chat detail mode */}
      {!isChatDetail && <Navigation
        viewMode={viewMode}
        isAdmin={isAdmin}
        isModerator={isModerator}
        isSupport={isSupport}
      />}
    </div>
    </CommunityTourContext.Provider>
  );
};

export default AppLayout;